import { FC, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import { useTranslation } from 'react-i18next';
import React from 'react';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import EventIcon from '@mui/icons-material/Event';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BiotechTwoToneIcon from '@mui/icons-material/BiotechTwoTone';
import InventoryTwoToneIcon from '@mui/icons-material/InventoryTwoTone';
import ReceiptLongTwoToneIcon from '@mui/icons-material/ReceiptLongTwoTone';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import BrokenImageTwoToneIcon from '@mui/icons-material/BrokenImageTwoTone';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';
import Scrollbars from "react-custom-scrollbars-2";
import { PatientHistoryListDTO } from './PatientHistoryWidget';
import { DocumentDashboardDTO, PatientHistoryItemDTO } from '../../../../../api/DashboardWidgetDTO';
import { DocumentClient } from '../../../../../api/Document/DocumentClient';
import { REACT_APP_PORTAL_API_URL, REACT_APP_PUBLIC_API_URL } from '../../../../../KeyVariables';
import { useNavigate } from 'react-router-dom';
import MedicalAnalysisBatchDialog from './MedicalAnalysisBatchDialog';

const iconFontSize = 32;
const iconMapping: Record<string, JSX.Element> = {
  analyzis: <BiotechTwoToneIcon sx={{ fontSize: iconFontSize }} />,
  consultation: <InventoryTwoToneIcon sx={{ fontSize: iconFontSize }} />,
  prescription: <ReceiptLongTwoToneIcon sx={{ fontSize: iconFontSize }} />,
  investigation: <SearchTwoToneIcon sx={{ fontSize: iconFontSize }} />,
  dicom: <BrokenImageTwoToneIcon sx={{ fontSize: iconFontSize }} />
};

const ListColumnWrapper = styled(Card)(
  ({ theme }) => `
      border-top-width: 8px;
      border-top-style: solid;
  `
);
const AvatarLabelSecondary = styled(Avatar)(
  ({ theme }) => `
      background-color: ${theme.colors.alpha.black[5]};
      color: ${theme.colors.primary.main};
      height: ${theme.spacing(5.5)};
`
);
interface ResultsProps {
  list: PatientHistoryListDTO;
  loading: boolean;
  patientId: number;
}

const PatientHistoryTable: FC<ResultsProps> = ({ list, loading, patientId }) => {
  const { t }: { t: any } = useTranslation('Portal');
  const iframeRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openDialog, setOpenDialog] = useState(false);
  const [openAnalysisDialog, setOpenAnalysisDialog] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<number | undefined>();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentDashboardDTO[]>([]);
  const navigate = useNavigate();
  
  const handleOpenDialog = (documents: DocumentDashboardDTO[]) => {
    setSelectedDocuments(documents);
    console.log(documents)
    setOpenDialog(true);
  };

  const handleOpenAnalysisDialog = (batchId: number) => {
    setOpenAnalysisDialog(true);
    setSelectedBatch(batchId);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCloseAnalysisDialog = () => {
    setOpenAnalysisDialog(false);
    setSelectedBatch(undefined);
  };
  
  const handleSchedule = (item : PatientHistoryItemDTO) => {
    navigate(`/calendar?serviceId=${item.serviceId}&${item.type}Id=${item.id}` + (item.medicalProfileId ? `&medicalProfileId=${item.medicalProfileId}` : ''));
  }

  const handleDownload = async (doc: DocumentDashboardDTO, type: string) => {
    try {
      setDownloadLoading(true);
      const pdf = (await new DocumentClient(REACT_APP_PORTAL_API_URL).getdocument(doc.identity!)).data;
      const blob = new Blob([pdf], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      const newTab = window.open();
        if (newTab) {
            newTab.location.href = url;
        } else {
            window.location.href = url;
        }


      // if (type === "download") {
      //   const url = window.URL.createObjectURL(new Blob([pdf]));
      //   const link = document.createElement('a');
      //   link.href = url;
      //   link.setAttribute('download', doc.name!);
      //   document.body.appendChild(link);
      //   link.click();
      //   link.parentNode?.removeChild(link);
      // } else if (type === "newtab") {

      // } else if (type === "print") {
      //   const url = window.URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));
      //   const iframe = iframeRef.current;
      //   iframe.onload = () => {
      //     iframe.contentWindow.print();
      //     window.URL.revokeObjectURL(url);
      //     iframe.onload = null;
      //   };
      //   iframe.src = url;
      // }

    } catch (error) {
      console.error('Failed to download document:', error);
    }
    finally {
      setDownloadLoading(false);
    }
  };


  return (
    <ListColumnWrapper
      sx={{
        borderColor: list.color,
        mt: 1
      }}
    >
      <iframe ref={iframeRef} style={{ display: 'none' }} title="PDF Viewer" />
      <Box
        px={2}
        pt={1}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box display="flex"
          justifyContent="space-between">
          <Typography color="inherit" variant={isMobile ? "h5" : "h4"}>
            {list.name}
          </Typography>
          {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Box>

        <Box display="flex" alignItems="center" mb={1}>
          <Button variant="contained" size="small" onClick={() => window.open('/patient-archive?Tab=consults' + (patientId > 0 ? `&PatientId=${patientId}` : ''), '_self')}>
            {t('Portal_Archive')}
          </Button>
        </Box>
      </Box>
      <Box
      >
        <Divider />
        <Scrollbars autoHeight autoHide autoHeightMax={isMobile ? '35vh' : "50vh"}>

          <Box>
            <Timeline placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              {list.items && list.items.length > 0 ? list.items?.map((item, index) => (
                <TimelineItem key={index}>
                  <TimelineSeparator>
                    <AvatarLabelSecondary>
                      {iconMapping[item.type!]}
                    </AvatarLabelSecondary>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: isMobile ? 1 : 3 }}>
                          {
                            list.showTime &&
                            <Typography noWrap variant="body2" >
                              {item.date && format(item.date!, 'HH:mm')}
                            </Typography>
                          }
                          <Typography variant={isMobile ? "body2" : "h5"} noWrap>
                            {item.date && format(item.date!, 'dd.MM.yyyy')}
                          </Typography>
                          {
                            list.showTime && item.clinic &&
                            <Typography noWrap variant="body2" >
                              {item.clinic.name}
                            </Typography>
                          }
                        </Box>
                        <Box display="flex" alignItems="center">
                          {item.doctorDashboard?.image && list.showDoctor && (
                            <Avatar src={REACT_APP_PUBLIC_API_URL + "/image/" + item.doctorDashboard.image} sx={{ mr: 1 }} />
                          )}
                          <Box>
                            {
                              list.showDoctor &&
                              <Typography variant="body2" noWrap>
                                {item.doctorDashboard?.name}
                              </Typography>
                            }
                            <Typography variant="body1" >
                              {t(item.serviceDashboard?.name)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Box display="flex" gap={1}>
                        {item.enableSchedule && (
                          <IconButton size="small" color="primary" onClick={() => handleSchedule(item)}>
                            <EventIcon />
                          </IconButton>
                        )}
                        {item.type == "analyzis" && (
                          <IconButton size="small" color="primary" onClick={() => handleOpenAnalysisDialog(item.id)}>
                            <VisibilityIcon />
                          </IconButton>
                        )}
                        {item.documents?.length! > 0 && (
                          <IconButton size="small" color="secondary" onClick={() => handleOpenDialog(item.documents!)}>
                            <Badge badgeContent={item.documents?.length} color="error">
                              <AttachFileIcon />
                            </Badge>
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    {item.images?.length! > 0 && (
                      <Box display="flex" flexWrap="wrap" mt={1} justifyContent={"left"} gap={1} ml={11}>
                        {item.images!.map((image, index) => (
                          <Avatar onClick={() => image.link ? window.open(image.link, '_self') : console.log("No link for this image")}
                            key={index} src={REACT_APP_PUBLIC_API_URL + "/image/" + image.identity!} sx={{ width: 70, height: 70, cursor: "pointer" }} />
                        ))}
                      </Box>
                    )}
                  </TimelineContent>
                </TimelineItem>

              )) :
                <Typography>{t('Portal_MissingServices')} {list.name.toLowerCase()}.</Typography>
              }
            </Timeline>
          </Box>
        </Scrollbars>

      </Box>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg">
        <DialogTitle>{t('Portal_DocumentsToDownload')}</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableBody>
                {selectedDocuments.map((doc, index) => (
                  <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "initial", cursor: 'pointer' }}
                  onClick={() => handleDownload(doc, "newtab")}>
                    <TableCell align="center">
                      <FileOpenIcon />
                    </TableCell>
                    <TableCell>
                      <Typography>{doc.name}</Typography>
                    </TableCell>
                    {/* <TableCell align="center">
                      <IconButton onClick={() => handleDownload(doc, "print")}>
                        <PrintIcon fontSize="medium" />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => handleDownload(doc, "download")}>
                        <DownloadIcon fontSize="medium" />
                      </IconButton>
                    </TableCell> */}
                    {/* <TableCell align="center">
                      <IconButton onClick={() => handleDownload(doc, "newtab")}>
                        <OpenInNewIcon fontSize="medium" />
                      </IconButton>
                    </TableCell> */}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('Închide')}</Button>
        </DialogActions>
      </Dialog>
      <MedicalAnalysisBatchDialog
        handleClose={handleCloseAnalysisDialog}
        open={openAnalysisDialog}
        medicalAnalysisBatchId={selectedBatch}
      />
    </ListColumnWrapper>
  );
};

export default PatientHistoryTable;
